import { useState } from "react"
import { Alert, Collapse, Form, Input, Modal, Select, Spin, message } from "antd"
import TenantDeviceApi from "../../../../../../../api/TenantDeviceApi";
import SleepUtils from "../../../../../../../util/SleepUtils";
import { InvokeLockoutModalWrapper, InvokeLockoutParam, InvokeLockoutParamInput, InvokeLockoutParamLabel, InvokeLockoutParams, InvokeLockoutResponse, InvokeLockoutResponseRaw, InvokeLockoutResponseTitle, InvokeLockoutTips, InvokeLockoutResponseParam, InvokeLockoutResponseParamLabel, InvokeLockoutResponseParamValue, InvokeLockoutResponseParams, InvokeLockoutDeviceName } from "./InvokeLockoutModal.styled";

interface InvokeLockoutModalProps {
    tenantDeviceView: any,
    open: boolean,
    onClose: () => void,
}

const InvokeLockoutModal: React.FC<InvokeLockoutModalProps> = (props)=> {
    let tenantDeviceApi = new TenantDeviceApi();
    let [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    
    // states
    let [openDoubleConfirmModal, setOpenDoubleConfirmModal] = useState<boolean>(false);
    let [lockoutType, setLockoutType] = useState<number>(null);
    let [adminPassword, setAdminPassword] = useState<string>("");
    let [invokeResponse, setInvokeResponse] = useState<any>(null);
    let [executing, setExecuting] = useState<boolean>(false);
    let [executeResult, setExecuteResult] = useState<string>("");

    let lockoutTypeOptions = [
        {label: '正常', value: 0},
        {label: '一级锁机', value: 1},
        {label: '二级锁机', value: 2},
    ];

    // functions
    let onClose = ()=> {
        props.onClose();
        form.resetFields();
        setLockoutType(null);
        setAdminPassword("");
        setInvokeResponse(null);
        setExecuting(false);
        setExecuteResult("");
    };

    let onSubmit = () => {
        form.validateFields().then(async (values)=> {
            setOpenDoubleConfirmModal(true);
        }).catch((err)=> {
            console.log(err);
        });
    };

    let onDoubleConfirm = async () => {
        try {
            setExecuting(true);
            await execute();
        } finally {
            setExecuting(false);
        }
    };

    let execute = async ()=> {
        // close double confirm modal first
        setOpenDoubleConfirmModal(false);
        
        // submit
        let submitResp = (await tenantDeviceApi.invokeDeviceService({
            sn: props.tenantDeviceView.tenantNode.sn,
            service: "lockout",
            params: {
                type: lockoutType,
            },
            password: adminPassword,
        })).data.data;
        let invokeId = submitResp.id

        // wait for response
        let retryTimes = 10;
        for(let i=0; i<retryTimes; i++) {
            // wait for 1 second
            await SleepUtils.sleep(1000);
            
            // get response
            let invokeResp = (await tenantDeviceApi.getInvokeDeviceResponse(invokeId)).data.data;
            if (invokeResp != null) {
                setInvokeResponse(invokeResp);
                if (invokeResp.ret == 0) {
                    messageApi.success("执行成功");
                    setExecuteResult("执行成功");
                } else {
                    messageApi.error("执行失败！");
                    setExecuteResult("执行失败！");
                }
                break;
            }

            // timeout 
            if (i == retryTimes-1) {
                messageApi.error("操作超时！");
                setExecuteResult("操作超时！");
            }
        }
    };

    // render
    return <Modal open={props.open}
        closable={false}
        destroyOnClose={true}
        afterClose={() => onClose()}
        onCancel={()=> onClose()}
        onOk={() => onSubmit()}
        width={600}
        style={{top: '150px'}}
        title="发送锁机指令"
    >
        <InvokeLockoutModalWrapper>
            {contextHolder}
            <InvokeLockoutDeviceName>目标设备：{props.tenantDeviceView.device?.name}</InvokeLockoutDeviceName>
            <InvokeLockoutTips>当前操作将发送锁机信号，请重复确认当前设备是否目标设备。</InvokeLockoutTips>

            <Form form={form} name="form" layout="vertical">
                <InvokeLockoutParams>
                    <InvokeLockoutParam>
                        <Form.Item name="lockoutType" label="锁机状态" rules={[{required: true}]} initialValue={lockoutType}>
                            <Select
                                style={{width: '100%'}}
                                placeholder="请输入锁机状态"
                                value={lockoutType}
                                onChange={(selectedValue)=> setLockoutType(selectedValue)}
                                options={lockoutTypeOptions}
                            />
                        </Form.Item>
                    </InvokeLockoutParam>
                    <InvokeLockoutParam>
                        <Form.Item name="adminPassword" label="操作密码" rules={[{required: true}]} initialValue={adminPassword}>
                            <Input.Password placeholder="请输入管理员密码" value={adminPassword} onChange={(e)=> setAdminPassword(e.target.value)}/>
                        </Form.Item>
                    </InvokeLockoutParam>
                </InvokeLockoutParams>
            </Form>
            
            <Modal open={openDoubleConfirmModal}
                destroyOnClose={true}
                closable={false}
                afterClose={() => setOpenDoubleConfirmModal(false)}
                onCancel={()=> setOpenDoubleConfirmModal(false)}
                onOk={() => onDoubleConfirm()}
                width={400}
                style={{top: '200px'}}
                title="再次确认"
            >
                <InvokeLockoutTips>当前操作为高危操作，请再次确认。</InvokeLockoutTips>
            </Modal>

            <Spin spinning={executing} />
            {executeResult && <Alert type="error" message={executeResult} /> }

            { invokeResponse && <InvokeLockoutResponse>
                <InvokeLockoutResponseTitle>执行结果</InvokeLockoutResponseTitle>
                
                <InvokeLockoutResponseParams>
                    <InvokeLockoutResponseParam>
                        <InvokeLockoutResponseParamLabel>返回码（0: 成功，其它：失败）</InvokeLockoutResponseParamLabel>
                        <InvokeLockoutResponseParamValue>{invokeResponse.ret}</InvokeLockoutResponseParamValue>
                    </InvokeLockoutResponseParam>
                </InvokeLockoutResponseParams>

                <InvokeLockoutResponseRaw>
                    <Collapse
                        size="small"
                        style={{width: '100%'}}
                        items={[{
                            key: 'raw', 
                            label: '原始响应数据', 
                            children: <pre style={{margin: '0px'}}>{JSON.stringify(invokeResponse, null, 4)}</pre> 
                        }]}
                    />
                </InvokeLockoutResponseRaw>
            </InvokeLockoutResponse> }
        </InvokeLockoutModalWrapper>
    </Modal>
};

export default InvokeLockoutModal;